<script setup lang="ts">
import { DateTime } from 'luxon'
import type { HardwareProfile } from '@/stores/type/calculation.type'

const props = defineProps<{
  item: HardwareProfile
  canManage: boolean
}>()

const emit = defineEmits<{
  edit: [item: HardwareProfile]
  delete: [item: HardwareProfile]
}>()

const formatNumber = (value: number) => {
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
}

const formatDate = (value?: string) => {
  if (!value) return '-'
  return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED)
}

const regionLabels: Record<string, string> = {
  westeurope: 'West Europe',
  northeurope: 'North Europe',
  uksouth: 'UK South',
  ukwest: 'UK West',
  francecentral: 'France Central',
  germanywestcentral: 'Germany West Central',
  swedencentral: 'Sweden Central',
  norwayeast: 'Norway East',
  switzerlandnorth: 'Switzerland North',
  polandcentral: 'Poland Central',
  italynorth: 'Italy North',
  spaincentral: 'Spain Central'
}

const formatRegion = (value?: string) => {
  if (!value) return regionLabels.westeurope
  return regionLabels[value] ?? value
}

const formatVmImage = (value?: string) => {
  if (!value) return '-'
  if (value === 'UBUNTU_2404_LTS_SERVER') return 'Ubuntu 24.04 LTS Server'
  return value
}

const osDiskTypeLabels: Record<string, string> = {
  Standard_LRS: 'Standard HDD LRS',
  StandardSSD_LRS: 'Standard SSD LRS',
  Premium_LRS: 'Premium SSD LRS'
}

const formatOsDisk = (item: HardwareProfile) => {
  const size = item.vm_os_disk_size_gb ?? 32
  const type =
    osDiskTypeLabels[item.vm_os_disk_storage_account_type ?? ''] ??
    item.vm_os_disk_storage_account_type
  return `${size} GB ${type || ''}`.trim()
}
</script>

<template>
  <tr>
    <td class="name-column font-weight-medium">{{ item.name }}</td>
    <td class="description-column" :title="item.description || ''">
      {{ item.description || '-' }}
    </td>
    <td>{{ formatRegion(item.deployment_region) }}</td>
    <td>{{ item.vm_size || '-' }}</td>
    <td>{{ formatVmImage(item.vm_image) }}</td>
    <td>{{ formatOsDisk(item) }}</td>
    <td class="text-center">{{ item.spot_instance ? 'Yes' : 'No' }}</td>
    <td class="text-right">{{ formatNumber(item.cpu_cores) }}</td>
    <td class="text-right">{{ formatNumber(item.memory_gb) }} GB</td>
    <td>{{ item.project_name || 'Global' }}</td>
    <td>{{ formatDate(item.modified) }}</td>
    <td v-if="canManage" class="text-center action-column">
      <v-btn
        aria-label="Edit hardware profile"
        icon="mdi-pencil"
        variant="text"
        @click="emit('edit', item)"
      />
    </td>
    <td v-if="canManage" class="text-center action-column">
      <v-btn
        aria-label="Delete hardware profile"
        icon="mdi-trash-can-outline"
        variant="text"
        @click="emit('delete', item)"
      />
    </td>
  </tr>
</template>

<style scoped>
.name-column {
  min-width: 180px;
}

.description-column {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-column {
  width: 72px;
}
</style>
